import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  MessageSquare, Search, Plus, Phone, Mail, Calendar, User, Clock,
  RefreshCw, X, Edit, Trash2, CheckCircle, AlertCircle, Filter
} from 'lucide-react';
import api from '../api';
import { toast } from 'sonner';

const CommunicationLogPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientIdFilter = searchParams.get('patientId');
  
  const [logs, setLogs] = useState([]);
  const [patients, setPatients] = useState([]);
  const [stats, setStats] = useState({ total: 0, today: 0, thisWeek: 0, followUpPending: 0, byType: {} });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [formData, setFormData] = useState({
    patientId: patientIdFilter || '',
    patientName: '',
    communicationType: 'Phone Call',
    direction: 'Outgoing',
    contactPerson: '',
    contactRole: 'Patient',
    phoneNumber: '',
    email: '',
    subject: '',
    summary: '',
    outcome: '',
    followUpRequired: false,
    followUpDate: '',
    followUpNotes: '',
    staffName: '',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    duration: ''
  });

  const communicationTypes = ['Phone Call', 'Email', 'In-Person Visit', 'Video Call', 'Text Message', 'Fax', 'Letter/Mail', 'Voicemail'];
  const contactRoles = ['Patient', 'Family Member', 'Caregiver', 'Physician', 'Insurance', 'Pharmacy', 'Other Provider', 'Other'];

  useEffect(() => {
    fetchData();
  }, [patientIdFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [logsRes, patientsRes, statsRes] = await Promise.all([
        patientIdFilter 
          ? api.get(`/communication-logs/patient/${patientIdFilter}`)
          : api.get('/communication-logs'),
        api.get('/patients'),
        api.get('/communication-logs/stats')
      ]);
      
      if (logsRes.data.success) setLogs(logsRes.data.logs || []);
      if (patientsRes.data.success) setPatients(patientsRes.data.patients || []);
      if (statsRes.data.success) setStats(statsRes.data.stats);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load communication logs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.patientName || !formData.summary) {
      toast.error('Please fill in patient and summary');
      return;
    }
    
    try {
      if (editingLog) {
        await api.put(`/communication-logs/${editingLog.id}`, formData);
        toast.success('Communication log updated');
      } else {
        const res = await api.post('/communication-logs', formData);
        if (res.data.success) {
          setLogs(prev => [res.data.log, ...prev]);
          toast.success('Communication log created');
        }
      }
      closeModal();
      fetchData();
    } catch (error) {
      toast.error('Failed to save communication log');
    }
  };

  const handleDelete = async (logId) => {
    if (!window.confirm('Are you sure you want to delete this log?')) return;
    try {
      await api.delete(`/communication-logs/${logId}`);
      setLogs(prev => prev.filter(l => l.id !== logId));
      toast.success('Log deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete log');
    }
  };

  const openEditModal = (log) => {
    setEditingLog(log);
    setFormData({
      patientId: log.patientId || '',
      patientName: log.patientName || '',
      communicationType: log.communicationType || 'Phone Call',
      direction: log.direction || 'Outgoing',
      contactPerson: log.contactPerson || '',
      contactRole: log.contactRole || 'Patient',
      phoneNumber: log.phoneNumber || '',
      email: log.email || '',
      subject: log.subject || '',
      summary: log.summary || '',
      outcome: log.outcome || '',
      followUpRequired: log.followUpRequired || false,
      followUpDate: log.followUpDate || '',
      followUpNotes: log.followUpNotes || '',
      staffName: log.staffName || '',
      date: log.date || new Date().toISOString().split('T')[0],
      time: log.time || '',
      duration: log.duration || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingLog(null);
    setFormData({
      patientId: patientIdFilter || '',
      patientName: '',
      communicationType: 'Phone Call',
      direction: 'Outgoing',
      contactPerson: '',
      contactRole: 'Patient',
      phoneNumber: '',
      email: '',
      subject: '',
      summary: '',
      outcome: '',
      followUpRequired: false,
      followUpDate: '',
      followUpNotes: '',
      staffName: '',
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5),
      duration: ''
    });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Phone Call': return <Phone className="w-4 h-4" />;
      case 'Email': return <Mail className="w-4 h-4" />;
      case 'In-Person Visit': return <User className="w-4 h-4" />;
      default: return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Phone Call': return 'bg-blue-100 text-blue-800';
      case 'Email': return 'bg-purple-100 text-purple-800';
      case 'In-Person Visit': return 'bg-green-100 text-green-800';
      case 'Video Call': return 'bg-cyan-100 text-cyan-800';
      case 'Text Message': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      log.patientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.summary?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.contactPerson?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = !typeFilter || log.communicationType === typeFilter;
    return matchesSearch && matchesType;
  });

  return (
    <DashboardLayout>
      <div className="p-6" data-testid="communication-log-page">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-[#f39c12]" />
                Patient Communication Log
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                Track all patient interactions - calls, emails, visits, and more
                {patientIdFilter && <span className="ml-2 text-blue-600">(Filtered by patient)</span>}
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchData} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />Refresh
              </Button>
              <Button 
                className="bg-[#f39c12] hover:bg-[#e67e22] text-white" 
                onClick={() => setShowModal(true)}
                data-testid="new-log-btn"
              >
                <Plus className="w-4 h-4 mr-2" />Log Communication
              </Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-700">{stats.total}</div>
            <div className="text-sm text-blue-600">Total Logs</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-700">{stats.today}</div>
            <div className="text-sm text-green-600">Today</div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-purple-700">{stats.thisWeek}</div>
            <div className="text-sm text-purple-600">This Week</div>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-orange-700">{stats.byType['Phone Call'] || 0}</div>
            <div className="text-sm text-orange-600">Phone Calls</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-red-700">{stats.followUpPending}</div>
            <div className="text-sm text-red-600">Follow-ups Pending</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input 
                placeholder="Search by patient, contact, or summary..." 
                value={searchQuery} 
                onChange={(e) => setSearchQuery(e.target.value)} 
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select 
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm bg-white min-w-[150px]"
                data-testid="type-filter"
              >
                <option value="">All Types</option>
                {communicationTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            {patientIdFilter && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/dashboard/patients/communication')}
              >
                Clear Patient Filter
              </Button>
            )}
          </div>
        </div>

        {/* Logs Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-lg font-medium">No communication logs found</p>
              <p className="text-sm">Click "Log Communication" to record your first interaction</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date/Time</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Patient</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Summary</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Follow-up</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{log.date}</div>
                      <div className="text-sm text-gray-500">{log.time}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{log.patientName}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(log.communicationType)}`}>
                        {getTypeIcon(log.communicationType)}
                        {log.communicationType}
                      </span>
                      <div className="text-xs text-gray-500 mt-1">{log.direction}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{log.contactPerson || '-'}</div>
                      <div className="text-xs text-gray-500">{log.contactRole}</div>
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      <div className="text-sm text-gray-900 truncate" title={log.summary}>
                        {log.summary}
                      </div>
                      {log.outcome && (
                        <div className="text-xs text-gray-500 truncate">Outcome: {log.outcome}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {log.followUpRequired ? (
                        <div className="flex items-center gap-1">
                          <AlertCircle className="w-4 h-4 text-orange-500" />
                          <span className="text-xs text-orange-600">{log.followUpDate || 'Pending'}</span>
                        </div>
                      ) : (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => openEditModal(log)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDelete(log.id)} className="text-red-600 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 m-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[#f39c12]" />
                  {editingLog ? 'Edit Communication Log' : 'Log New Communication'}
                </h2>
                <button onClick={closeModal}><X className="w-5 h-5" /></button>
              </div>
              
              <div className="space-y-4">
                {/* Patient Selection */}
                <div>
                  <label className="block text-sm font-medium mb-1">Patient *</label>
                  <select 
                    value={formData.patientId}
                    onChange={(e) => {
                      const p = patients.find(p => p.id === e.target.value);
                      setFormData(prev => ({ 
                        ...prev, 
                        patientId: e.target.value, 
                        patientName: p ? `${p.firstName} ${p.lastName}` : '' 
                      }));
                    }}
                    className="w-full border rounded-md px-3 py-2"
                    data-testid="patient-select"
                  >
                    <option value="">Select Patient</option>
                    {patients.map(p => (
                      <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
                    ))}
                  </select>
                </div>

                {/* Communication Type & Direction */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Communication Type *</label>
                    <select 
                      value={formData.communicationType}
                      onChange={(e) => setFormData(prev => ({ ...prev, communicationType: e.target.value }))}
                      className="w-full border rounded-md px-3 py-2"
                      data-testid="comm-type-select"
                    >
                      {communicationTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Direction</label>
                    <select 
                      value={formData.direction}
                      onChange={(e) => setFormData(prev => ({ ...prev, direction: e.target.value }))}
                      className="w-full border rounded-md px-3 py-2"
                    >
                      <option value="Outgoing">Outgoing</option>
                      <option value="Incoming">Incoming</option>
                    </select>
                  </div>
                </div>

                {/* Date, Time, Duration */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Date</label>
                    <Input 
                      type="date" 
                      value={formData.date}
                      onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Time</label>
                    <Input 
                      type="time" 
                      value={formData.time}
                      onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Duration</label>
                    <Input 
                      placeholder="e.g., 15 min"
                      value={formData.duration}
                      onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                    />
                  </div>
                </div>

                {/* Contact Person & Role */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Contact Person</label>
                    <Input 
                      placeholder="Name of person contacted"
                      value={formData.contactPerson}
                      onChange={(e) => setFormData(prev => ({ ...prev, contactPerson: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Contact Role</label>
                    <select 
                      value={formData.contactRole}
                      onChange={(e) => setFormData(prev => ({ ...prev, contactRole: e.target.value }))}
                      className="w-full border rounded-md px-3 py-2"
                    >
                      {contactRoles.map(role => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Phone/Email */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone Number</label>
                    <Input 
                      placeholder="(555) 123-4567"
                      value={formData.phoneNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email</label>
                    <Input 
                      type="email"
                      placeholder="email@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium mb-1">Subject</label>
                  <Input 
                    placeholder="Brief topic of communication"
                    value={formData.subject}
                    onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  />
                </div>

                {/* Summary */}
                <div>
                  <label className="block text-sm font-medium mb-1">Summary *</label>
                  <textarea 
                    value={formData.summary}
                    onChange={(e) => setFormData(prev => ({ ...prev, summary: e.target.value }))}
                    className="w-full border rounded-md px-3 py-2"
                    rows={3}
                    placeholder="Detailed summary of the communication..."
                    data-testid="summary-textarea"
                  />
                </div>

                {/* Outcome */}
                <div>
                  <label className="block text-sm font-medium mb-1">Outcome/Result</label>
                  <Input 
                    placeholder="What was the outcome of this communication?"
                    value={formData.outcome}
                    onChange={(e) => setFormData(prev => ({ ...prev, outcome: e.target.value }))}
                  />
                </div>

                {/* Follow-up Section */}
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-center gap-2 mb-3">
                    <input 
                      type="checkbox"
                      checked={formData.followUpRequired}
                      onChange={(e) => setFormData(prev => ({ ...prev, followUpRequired: e.target.checked }))}
                      id="followup-checkbox"
                    />
                    <label htmlFor="followup-checkbox" className="text-sm font-medium text-orange-800">
                      Follow-up Required
                    </label>
                  </div>
                  {formData.followUpRequired && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1 text-orange-800">Follow-up Date</label>
                        <Input 
                          type="date"
                          value={formData.followUpDate}
                          onChange={(e) => setFormData(prev => ({ ...prev, followUpDate: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1 text-orange-800">Follow-up Notes</label>
                        <Input 
                          placeholder="What to follow up on"
                          value={formData.followUpNotes}
                          onChange={(e) => setFormData(prev => ({ ...prev, followUpNotes: e.target.value }))}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Staff Name */}
                <div>
                  <label className="block text-sm font-medium mb-1">Staff Name</label>
                  <Input 
                    placeholder="Your name"
                    value={formData.staffName}
                    onChange={(e) => setFormData(prev => ({ ...prev, staffName: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={closeModal}>Cancel</Button>
                <Button 
                  className="bg-[#f39c12] hover:bg-[#e67e22] text-white" 
                  onClick={handleSubmit}
                  data-testid="save-log-btn"
                >
                  {editingLog ? 'Update Log' : 'Save Log'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CommunicationLogPage;
